import { useTranslation } from 'react-i18next';
import {
  IconAvailability,
  IconChevron,
  IconEducation,
  IconLanguages,
  IconSkills,
  IconUser,
} from '../../../../app/components/Icons';
import type {
  MentorProfileStepId,
  MentorProfileStepProgress,
} from '../../application/mentorProfileSteps';

const STEP_ICONS = {
  basic: IconUser,
  experience: IconEducation,
  skills: IconSkills,
  languages: IconLanguages,
  availability: IconAvailability,
} satisfies Record<MentorProfileStepId, typeof IconUser>;

type MentorProfileStepsProps = {
  steps: MentorProfileStepProgress[];
  activeStepId: MentorProfileStepId;
  onStepSelect: (id: MentorProfileStepId) => void;
};

export function MentorProfileSteps({
  steps,
  activeStepId,
  onStepSelect,
}: MentorProfileStepsProps) {
  const { t } = useTranslation();

  return (
    <nav
      className="profile-wrapper__steps"
      aria-label={t('mentor-profile.steps_nav')}
    >
      {steps.map((step) => {
        const active = step.id === activeStepId;
        const Icon = STEP_ICONS[step.id];

        return (
          <button
            key={step.id}
            type="button"
            className={[
              'profile-step',
              active ? 'profile-step--active' : '',
              step.complete ? 'profile-step--complete' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            onClick={() => onStepSelect(step.id)}
            aria-current={active ? 'step' : undefined}
          >
            <span className="profile-step__indicator" aria-hidden>
              <span className="profile-step__indicator-dot" />
            </span>

            <span className="profile-step__icon-wrap" aria-hidden>
              <Icon className="profile-step__icon" />
            </span>

            <span className="profile-step__label">
              {t(`mentor-profile.steps.${step.id}`)}
            </span>

            <span className="profile-step__counter">
              {step.filled}/{step.total}
            </span>

            <span
              className={[
                'profile-step__arrow',
                active ? 'profile-step__arrow--visible' : '',
              ]
                .filter(Boolean)
                .join(' ')}
              aria-hidden
            >
              <IconChevron />
            </span>
          </button>
        );
      })}
    </nav>
  );
}

export default MentorProfileSteps;
